import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_URL from '../../api/baseURL';
import { useAuth } from '../../context/AuthContext';
import { FEATURES } from '../../config/permissions';
import { useTranslation } from 'react-i18next'; // Assuming i18n is used, otherwise remove
import StatCard from '../../components/StatCard'; // Reusing StatCard for uniform look

const ROLES = [
    "Car Owner",
    "Car Workshop",
    "Spare Parts Supplier",
    // "Insurance Company", // Add others as needed
];

const FeatureManagement = () => {
    const { token, user } = useAuth();
    const [permissionMap, setPermissionMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);

    // Initialize map with empty arrays
    useEffect(() => {
        fetchPermissions();
    }, []);

    const fetchPermissions = async () => {
        try {
            const res = await axios.get(`${API_URL}/permissions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPermissionMap(res.data);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: 'Failed to load permissions.' });
            setLoading(false);
        }
    };

    const toggleFeature = async (role, feature) => {
        const currentFeatures = permissionMap[role] || [];
        let newFeatures;

        if (currentFeatures.includes(feature)) {
            newFeatures = currentFeatures.filter(f => f !== feature);
        } else {
            newFeatures = [...currentFeatures, feature];
        }

        // Optimistic UI update
        setPermissionMap(prev => ({ ...prev, [role]: newFeatures }));

        try {
            await axios.post(`${API_URL}/permissions/update`, {
                role,
                features: newFeatures
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Optionally show success toast
        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: 'Failed to update permission.' });
            // Revert on failure
            fetchPermissions();
        }
    };

    if (loading) return <div className="p-10 text-white">Loading...</div>;

    return (
        <div className="min-h-screen bg-slate-900 text-white p-4 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-8">
                    Feature Access Management
                </h1>

                {message && (
                    <div className={`p-4 mb-6 rounded-lg ${message.type === 'error' ? 'bg-red-500/20 text-red-200' : 'bg-green-500/20 text-green-200'}`}>
                        {message.text}
                    </div>
                )}

                <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 shadow-xl overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="text-slate-400 border-b border-slate-700/50">
                                <th className="p-4 font-medium min-w-[200px]">Feature</th>
                                {ROLES.map(role => (
                                    <th key={role} className="p-4 font-medium text-center min-w-[120px]">{role}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {Object.values(FEATURES).map(feature => (
                                <tr key={feature} className="hover:bg-slate-700/30 transition-colors">
                                    <td className="p-4">
                                        <span className="font-mono text-sm px-2 py-1 bg-slate-900 rounded text-blue-300">
                                            {feature}
                                        </span>
                                        <p className="text-xs text-slate-500 mt-1">
                                            {feature.replace(/_/g, ' ')}
                                        </p>
                                    </td>
                                    {ROLES.map(role => {
                                        const isEnabled = (permissionMap[role] || []).includes(feature);
                                        return (
                                            <td key={`${role}-${feature}`} className="p-4 text-center">
                                                <button
                                                    onClick={() => toggleFeature(role, feature)}
                                                    className={`
                            relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out focus:outline-none
                            ${isEnabled ? 'bg-emerald-500' : 'bg-slate-600'}
                          `}
                                                >
                                                    <span
                                                        className={`
                              inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ease-in-out mt-1
                              ${isEnabled ? 'translate-x-3' : '-translate-x-3'}
                            `}
                                                    />
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default FeatureManagement;
